import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useSelector } from 'react-redux';
import { FC, ReactNode, createContext, useContext, useState } from 'react';
import { useTranslate } from 'hooks/useTranslate';

import actionTypes from 'constants/actionTypes';

import { CoOwner, Encumbrance } from 'types';

import checkIsValidEmail from 'utils/checkIsValidEmail';
import successSwal from 'utils/successSwal';
import errorSwal from 'utils/errorSwal';

import { useGetPartnerOptions } from 'modules/partners/hooks/useGetPartnerOptions';
import { CustomAsyncSelectOption } from 'modules/_shared/components/CustomAsyncSelect/types/CustomAsyncSelectOption';
import { setModal } from 'modules/_shared/redux/modalActions';

import {
  createCoOwner,
  updateCoOwner,
} from 'modules/partners/redux/coOwnerActions';
import { checkIsValidPercent } from '../utils/checkIsValidPercent';
import { AddCoOwnerModalContextType } from '../types/AddCoOwnerModalContextType';
import { addCoOwnerModalInitValue } from '../constants/addCoOwnerModalInitValue';
import { checkHasMemberSelector } from '../utils/checkHasMemberSelector';

const AddCoOwnerModalContext = createContext<AddCoOwnerModalContextType>(
  addCoOwnerModalInitValue
);

export const AddCoOwnerModalProvider: FC<{
  children: ReactNode;
  encumbrance: Encumbrance;
  coOwner?: CoOwner;
  modalActionType?: string;
  onAddCoOwnerSuccess?: () => void;
  onEditCoOwnerSuccess?: () => void;
}> = ({
  children,
  encumbrance,
  coOwner,
  modalActionType = actionTypes.ADD,
  onAddCoOwnerSuccess,
  onEditCoOwnerSuccess,
}) => {
  const { t } = useTranslate();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );

  const [partnerId, setPartnerId] = useState<string | null>(
    coOwner?.partner || null
  );
  const [name, setName] = useState<string>(coOwner?.name || '');
  const [cif, setCif] = useState<string>(coOwner?.cif || '');
  const [email, setEmail] = useState<string>(coOwner?.email || '');
  const [percentage, setPercentage] = useState<number>(
    coOwner?.percentage || 0
  );
  const [comments, setComments] = useState<string>(coOwner?.comment || '');
  const [isValidPercent, setIsValidPercent] = useState<boolean>(!!coOwner);
  const [isValidEmail, setIsValidEmail] = useState<boolean>(!!coOwner);
  const [hasBeenEdited, setHasBeenEdited] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    data: partnerOptionsData,
    actions: partnerOptionsActions,
    isLoading: isLoadingPartnerOptions,
  } = useGetPartnerOptions({
    partnerId: coOwner?.partner,
    societyId: actualSociety?._id,
  });

  const updateSelectedPartner = (option: CustomAsyncSelectOption | null) => {
    setPartnerId(option?.value || null);
    setName(option?.name || '');
    setCif(option?.cif || '');
    setEmail(option?.email || '');
  };

  const handleCloseModal = () => {
    store.dispatch(setModal(null));
  };

  const handleChangeSelectedPartner = (
    option: CustomAsyncSelectOption | null
  ) => {
    partnerOptionsActions.handleChangePartnerOption(option);
    updateSelectedPartner(option);
    const isValidEmail = checkIsValidEmail(option?.email || '');
    setIsValidEmail(isValidEmail);
    setHasBeenEdited(true);
  };

  const handleChangeName = (value: string) => {
    setName(value);
    setHasBeenEdited(true);
  };

  const handleChangeCif = (value: string) => {
    setCif(value);
    setHasBeenEdited(true);
  };

  const handleChangeEmail = (value: string) => {
    setEmail(value);

    const isValidEmail = checkIsValidEmail(value);
    setIsValidEmail(isValidEmail);
    setHasBeenEdited(true);
  };

  const handleChangePercentage = (newPwecent: number) => {
    setPercentage(newPwecent);

    const actualPercent = coOwner
      ? encumbrance?.coOwnersPercentage - coOwner.percentage
      : encumbrance?.coOwnersPercentage || 0;

    const isValidPercent = checkIsValidPercent({
      percent: newPwecent,
      actualPercent,
    });
    setIsValidPercent(isValidPercent);

    setHasBeenEdited(true);
  };

  const handleChangeComments = (value: string) => {
    setComments(value);
    setHasBeenEdited(true);
  };

  const buildCoOwnerData = (): Omit<
    CoOwner,
    '_id' | 'encumbrance' | 'society'
  > => ({
    partner: partnerId || undefined,
    name,
    cif,
    email,
    percentage,
    comment: comments,
  });

  const handleAddCoOwner = async () => {
    try {
      setIsLoading(true);

      const coOwnerData: Omit<CoOwner, '_id'> = {
        ...buildCoOwnerData(),
        encumbrance: encumbrance?._id,
        society: actualSociety?._id,
      };

      await store.dispatch(
        createCoOwner({
          coOwnerData,
        })
      );

      onAddCoOwnerSuccess?.();
      successSwal(t('CoOwnerAddedSuccessfully'));
    } catch (error) {
      errorSwal(t('ErrorAddingCoOwner'));
    } finally {
      setIsLoading(false);
      handleCloseModal();
    }
  };

  const handleEditCoOwner = async () => {
    if (!coOwner?._id) {
      return;
    }

    try {
      setIsLoading(true);

      await store.dispatch(
        updateCoOwner({
          coOwnerId: coOwner._id,
          updateCoOwnerData: buildCoOwnerData(),
        })
      );

      onEditCoOwnerSuccess?.();
      successSwal(t('CoOwnerUpdatedSuccessfully'));
    } catch (error) {
      errorSwal(t('ErrorUpdatingCoOwner'));
    } finally {
      setIsLoading(false);
      handleCloseModal();
    }
  };

  const handleSave =
    modalActionType === actionTypes.EDIT ? handleEditCoOwner : handleAddCoOwner;

  return (
    <AddCoOwnerModalContext.Provider
      value={{
        data: {
          modalActionType,
          selectedPartner: partnerOptionsData.selectedPartnerOption,
          name,
          cif,
          email,
          percentage,
          comments,
          partnerOptions: partnerOptionsData.partnerOptions,
          hasMembersSelector: checkHasMemberSelector(encumbrance.type),
          isValidPercent,
          isValidEmail,
          hasBeenEdited,
          isLoading: isLoading || isLoadingPartnerOptions,
        },
        actions: {
          handleCloseModal,
          handleChangeSelectedPartner,
          handleChangeName,
          handleChangeCif,
          handleChangeEmail,
          handleChangePercentage,
          handleChangeComments,
          handleFetchPartnersByTerm:
            partnerOptionsActions.handleSearchPartnerByTerm,
          handleSave,
        },
      }}
    >
      {children}
    </AddCoOwnerModalContext.Provider>
  );
};

export const useAddCoOwnerModal = () => {
  const context = useContext(AddCoOwnerModalContext);
  if (!context) {
    throw new Error(
      'useAddCoOwnerModal must be used within a AddCoOwnerModalProvider'
    );
  }
  return context;
};
